import { Form, Pagination, Table, notification } from 'antd';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import PageTitle from 'components/page-title';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { formatData } from 'utils/common';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { refreshPendingCounts } from 'layouts/pending-counts';
import { LeaveRequest, LeaveRequestFilter, LeaveRequestStatus } from 'types/leave-request';
import {
  LinkedFilterTag,
  toEnumValue,
  toPositiveInt,
  useDropSearchParams,
  useParamChange,
} from 'pages/user-manager/helpers/user-deep-link';
import LeaveRequestFilterForm, {
  LeaveRequestFilterFormValue,
} from './components/leave-request-filter';
import LeaveRequestDetailDrawer from './components/leave-request-detail-drawer';
import ReviewLeaveRequestModal, { ReviewAction } from './components/review-leave-request-modal';
import {
  LEAVE_REQUEST_TABLE_WIDTH,
  getLeaveRequestColumns,
} from './components/leave-request-table-columns';
import { mapLeaveRequestError } from './helpers/leave-request-error';

const LIMIT = 20;

const initFilter: LeaveRequestFilter = {
  page: 1,
  limit: LIMIT,
  status: LeaveRequestStatus.PENDING,
  from: dayjs().startOf('month').format('YYYY-MM-DD'),
  to: dayjs().endOf('month').format('YYYY-MM-DD'),
};

/**
 * Liên kết sâu: ?userId=<id>&name=<tên> và/hoặc ?status=PENDING|APPROVED|REJECTED|CANCELLED|ALL.
 * Có tham số → bỏ khoảng ngày mặc định (tháng này) để thấy đủ đơn của người/trạng thái đó;
 * có userId mà không có status → mọi trạng thái. Không có tham số → như trước.
 */
function readDeepLink(params: URLSearchParams) {
  const userId = toPositiveInt(params.get('userId'));
  const hasStatus = params.has('status');
  if (!userId && !hasStatus) return null;
  const status = toEnumValue(LeaveRequestStatus, params.get('status'));
  const name = params.get('name') || `#${userId}`;
  const filter: LeaveRequestFilter = formatData({ page: 1, limit: LIMIT, userId, status });
  const preset: Partial<LeaveRequestFilterFormValue> = {
    status,
    range: null,
    userId: userId ? { value: userId, label: name } : undefined,
  };
  return { filter, preset };
}

function extractList(res: any) {
  const body = res?.data || {};
  return {
    items: body.data || body.items || (Array.isArray(body) ? body : []),
    total: body.metadata?.total || body.meta?.total || body.total || 0,
  };
}

const LeaveRequestManager = () => {
  const [searchParams] = useSearchParams();
  const dropSearchParams = useDropSearchParams();
  const [deepLink] = useState(() => readDeepLink(searchParams));
  const [filterForm] = Form.useForm<LeaveRequestFilterFormValue>();
  const linkedUserId = toPositiveInt(searchParams.get('userId'));
  const linkedUserLabel = searchParams.get('name') || `#${linkedUserId}`;
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<LeaveRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<LeaveRequestFilter>(deepLink?.filter || initFilter);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  const [reviewTarget, setReviewTarget] = useState<LeaveRequest | null>(null);
  const [reviewAction, setReviewAction] = useState<ReviewAction | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { isAdmin, isMod } = useCurrentRoles();
  const canReview = isAdmin || isMod;

  const triggerRefresh = () => setRefreshKey((key) => key + 1);

  useEffect(() => {
    let cancelled = false;
    const fetchRequests = async () => {
      setLoading(true);
      try {
        const res = await AxiosUtils.get(API.leaveRequests, filter);
        const next = extractList(res);
        if (!cancelled) {
          setItems(next.items);
          setTotal(next.total);
        }
      } catch (err) {
        if (!cancelled) notification.error({ message: mapLeaveRequestError(err) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRequests();
    return () => {
      cancelled = true;
    };
  }, [filter, refreshKey]);

  // URL đổi sau khi trang đã mở (đóng thẻ "Đang lọc", bấm lại menu, Back/Forward).
  useParamChange(linkedUserId, (next, prev) => {
    if (next) {
      setFilter((p) => ({ ...p, userId: next, page: 1 }));
      filterForm.setFieldValue('userId', { value: next, label: linkedUserLabel });
      return;
    }
    setFilter((p) => (p.userId === prev ? formatData({ ...p, userId: undefined, page: 1 }) : p));
    if (filterForm.getFieldValue('userId')?.value === prev) {
      filterForm.setFieldValue('userId', undefined);
    }
  });

  const onFilterSubmit = (value: Partial<LeaveRequestFilter>) => {
    setFilter((prev) => formatData({ ...prev, ...value, page: 1 }));
    // Chọn người khác / Xoá lọc → bỏ ?userId= cũ khỏi URL để F5 không lọc lại người cũ.
    if (linkedUserId && value.userId !== linkedUserId) dropSearchParams('userId', 'name');
  };

  const openReview = (request: LeaveRequest, action: ReviewAction) => {
    setReviewTarget(request);
    setReviewAction(action);
  };

  const onReviewSuccess = (request: LeaveRequest) => {
    triggerRefresh();
    refreshPendingCounts();
    if (detailId === request.id) setDetailRefreshKey((key) => key + 1);
  };

  const onRaceCondition = (request: LeaveRequest) => {
    triggerRefresh();
    refreshPendingCounts();
    if (detailId === request.id) setDetailRefreshKey((key) => key + 1);
  };

  const columns = getLeaveRequestColumns({
    canReview,
    onDetail: setDetailId,
    onReview: openReview,
  });

  return (
    <div>
      <PageTitle title="Duyệt phiếu nghỉ phép" />
      <div style={{ marginBottom: 24 }}>
        <LeaveRequestFilterForm
          form={filterForm}
          preset={deepLink?.preset}
          onSubmit={onFilterSubmit}
        />
      </div>
      {linkedUserId && filter.userId === linkedUserId && (
        <LinkedFilterTag
          label={linkedUserLabel}
          onClose={() => dropSearchParams('userId', 'name')}
        />
      )}
      <div className="custom-table custom-form">
        <Table
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={items}
          loading={loading}
          scroll={{ x: LEAVE_REQUEST_TABLE_WIDTH }}
        />
        {total > LIMIT && (
          <Pagination
            showSizeChanger={false}
            current={filter.page}
            onChange={(page) => setFilter((prev) => ({ ...prev, page }))}
            showLessItems
            total={total}
            pageSize={LIMIT}
          />
        )}
      </div>
      <LeaveRequestDetailDrawer
        requestId={detailId}
        refreshKey={detailRefreshKey}
        canReview={canReview}
        onClose={() => setDetailId(null)}
        onReview={openReview}
      />
      <ReviewLeaveRequestModal
        request={reviewTarget}
        action={reviewAction}
        onClose={() => {
          setReviewTarget(null);
          setReviewAction(null);
        }}
        onSuccess={onReviewSuccess}
        onRaceCondition={onRaceCondition}
      />
    </div>
  );
};

export default LeaveRequestManager;
